import { useState } from 'react'
import {
  ShieldCheck,
  ShieldAlert,
  Pencil,
  Trash2,
  Info,
} from 'lucide-react'
import { patchJson } from '../lib/api.js'
import { Button, ChoiceChips, ConfirmDialog, DrawerFooter, ErrorState, Field, Modal, Textarea, fmtDateTime, useToast } from '../kit'

/**
 * URI (upper-respiratory-infection) screening — optional, per-visit, recorded
 * for accreditation. Stored as a single object on `chart.uri_screening`:
 *   { symptom: ["fever", ...], result: "no_risk"|"at_risk", note,
 *     created_at, created_by, modified_at, modified_by }
 *
 * `symptom` holds canonical tokens (NOT the Thai question text) so the wording
 * can change later without touching stored data. `result` is auto-derived:
 * any symptom ticked → "at_risk", otherwise "no_risk". Purely informational.
 *
 * Two pieces:
 *   - URIScreeningDialog — the popup checklist (create / edit / clear). Opened
 *     from the Documents section (when not yet screened) and from the card's
 *     "แก้ไข". Lives behind the Documents-section permission (intake-style).
 *   - URIScreeningEditor (default) — the view-only card. Renders ONLY once a
 *     screening exists; hidden otherwise so it doesn't clutter every chart.
 *
 * NOTE: the Xano `uri_screening.result` enum must include exactly the strings
 * "no_risk" and "at_risk" or the PATCH will be rejected.
 */

// token → Thai label. Add/relabel here without affecting stored data.
const SYMPTOMS = [
  { key: 'fever',       label: 'ไข้ (อุณหภูมิ ≥ 37.5°C)' },
  { key: 'cough',       label: 'ไอ' },
  { key: 'sore_throat', label: 'เจ็บคอ' },
  { key: 'runny_nose',  label: 'น้ำมูก / คัดจมูก' },
  { key: 'dyspnea',     label: 'เหนื่อยหอบ / หายใจลำบาก' },
]
const SYMPTOM_LABEL = Object.fromEntries(SYMPTOMS.map((s) => [s.key, s.label]))
const symptomText = (key) => SYMPTOM_LABEL[key] || key

const RESULT = {
  no_risk: { value: 'no_risk', label: 'ผ่าน — ไม่พบอาการ', bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', Icon: ShieldCheck },
  at_risk: { value: 'at_risk', label: 'พบความเสี่ยง',      bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   Icon: ShieldAlert },
}

export function isURIScreened(chart) {
  const s = chart?.uri_screening
  return !!(s && typeof s === 'object' && s.created_at)
}

/* -------------------------------------------------------------------------- */
/* Dialog — checklist popup (create / edit / clear)                           */
/* -------------------------------------------------------------------------- */

export function URIScreeningDialog({ chart, me, onClose, onSaved }) {
  const chartId = chart?.id
  const uid = me?.xano_id || 0
  const screening = chart?.uri_screening && typeof chart.uri_screening === 'object' ? chart.uri_screening : null
  const screened = isURIScreened(chart)

  const toast = useToast()
  const initialSymptoms = Array.isArray(screening?.symptom) ? screening.symptom : []
  const initialNote = screening?.note || ''
  const [symptoms, setSymptoms] = useState(() => initialSymptoms.slice())
  const [note, setNote] = useState(initialNote)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [confirmClear, setConfirmClear] = useState(false)

  const dirty = note !== initialNote
    || symptoms.length !== initialSymptoms.length
    || symptoms.some((k) => !initialSymptoms.includes(k))
  const selectedResult = symptoms.length ? 'at_risk' : 'no_risk'
  const toggle = (key) => setSymptoms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]))

  async function save() {
    if (!chartId) { setErr('ยังไม่มี chart ในเคสนี้'); return }
    setSaving(true); setErr(null)
    const now = Date.now()
    const base = screened
      ? { created_at: screening.created_at, created_by: screening.created_by, modified_at: now, modified_by: uid }
      : { created_at: now, created_by: uid }
    const next = { symptom: symptoms, result: selectedResult, note: note.trim(), ...base }
    try {
      await patchJson(`/api/charts/${chartId}`, { uri_screening: next })
      toast.success('บันทึกแล้ว')
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      setSaving(false)
    }
  }

  async function clearScreening() {
    if (!chartId) return
    setSaving(true); setErr(null)
    try {
      await patchJson(`/api/charts/${chartId}`, { uri_screening: null })
      toast.success('ลบผลคัดกรองแล้ว')
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'ลบไม่สำเร็จ'
      setErr(msg)
      toast.error('ลบไม่สำเร็จ', { detail: msg })
      setSaving(false)
    }
  }

  return (
    <Modal
      onClose={onClose}
      title="คัดกรอง URI"
      subtitle="คัดกรองการติดเชื้อทางเดินหายใจก่อนทำหัตถการ"
      busy={saving}
      dirty={dirty}
      footer={(
        <DrawerFooter
          onCancel={onClose}
          onSave={save}
          saving={saving}
          danger={screened && (
            <Button variant="danger-soft" size="sm" icon={Trash2} onClick={() => setConfirmClear(true)} disabled={saving}>
              ลบผลคัดกรอง
            </Button>
          )}
        />
      )}
    >
      {err && <ErrorState title={err} className="mb-3" />}

      <div className="space-y-4">
        <Field label="อาการที่พบ">
          {/* ChoiceChips hands back the whole new set; apply it as the single key that
              changed, through toggle(), so the stored order (click order) and any
              legacy token not in SYMPTOMS stay exactly as before. */}
          <ChoiceChips
            multiple
            options={SYMPTOMS.map((s) => ({ value: s.key, label: s.label, color: '#f59e0b' }))}
            values={symptoms}
            onChange={(next) => {
              const changed = SYMPTOMS.find((s) => next.has(s.key) !== symptoms.includes(s.key))
              if (changed) toggle(changed.key)
            }}
          />
        </Field>

        <div className="flex items-center gap-2 text-[13.5px]">
          <span className="text-slate-400">ผลคัดกรอง:</span>
          <ResultChip r={RESULT[selectedResult]} />
        </div>

        <Field label="หมายเหตุ" hint="เลือกใส่">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="เช่น มาตรการป้องกันที่ใช้ / แนะนำเลื่อนนัด"
            rows={2}
          />
        </Field>
      </div>

      {confirmClear && (
        <ConfirmDialog
          title="ลบผลคัดกรอง URI?"
          message="ผลการคัดกรองของเคสนี้จะถูกลบออกจาก chart"
          confirmLabel="ลบ"
          tone="danger"
          busy={saving}
          onConfirm={async () => {
            if (saving) return
            try { await clearScreening() } finally { setConfirmClear(false) }
          }}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Card — view-only; renders only once screened                               */
/* -------------------------------------------------------------------------- */

export default function URIScreeningEditor({ chart, canEdit, onEdit }) {
  const screening = chart?.uri_screening && typeof chart.uri_screening === 'object' ? chart.uri_screening : null
  if (!isURIScreened(chart)) return null
  const r = RESULT[screening.result] || RESULT.at_risk

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <ShieldCheck size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">URI Screening</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">คัดกรองการติดเชื้อทางเดินหายใจก่อนทำหัตถการ</div>
        </div>
        {canEdit && onEdit && (
          <Button variant="soft" size="sm" icon={Pencil} onClick={onEdit}>แก้ไข</Button>
        )}
      </header>

      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <ResultChip r={r} large />
          <ScreeningMetaPopup screening={screening} />
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-400 mb-1.5">อาการที่พบ</div>
          {Array.isArray(screening.symptom) && screening.symptom.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {screening.symptom.map((key) => (
                <span key={key} className="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                  {symptomText(key)}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[14px] text-slate-400 italic">ไม่พบอาการทางเดินหายใจ</span>
          )}
        </div>

        {screening.note && (
          <div>
            <div className="text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-400 mb-1">หมายเหตุ</div>
            <div className="text-[14px] text-slate-600 whitespace-pre-line">{screening.note}</div>
          </div>
        )}
      </div>
    </section>
  )
}

function ResultChip({ r, large }) {
  if (!r) return null
  const { Icon } = r
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ring-1 ${r.bg} ${r.text} ${r.ring} ${large ? 'px-3 py-1 text-[14px]' : 'px-2.5 py-0.5 text-[13px]'}`}>
      <Icon size={large ? 14 : 12} strokeWidth={2.25} />
      {r.label}
    </span>
  )
}

/* Meta popup — mirrors the consent / vital-sign info popups. */
function ScreeningMetaPopup({ screening }) {
  const [open, setOpen] = useState(false)
  if (!screening) return null
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="meta info"
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-all ${
          open ? 'text-brand-700 bg-brand-50 ring-1 ring-brand-200' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 ring-1 ring-transparent'
        }`}
      >
        <Info size={13} strokeWidth={2} />
      </button>
      {open && (
        <div role="tooltip" className="absolute right-0 bottom-full mb-2 z-20 min-w-[168px] rounded-lg bg-white ring-1 ring-brand-200 shadow-lg shadow-brand-900/10 overflow-hidden pointer-events-none animate-ss-pop-in">
          <MetaRow label="Screened">
            <span className="font-mono">{fmtDateTime(screening.created_at)}</span>
          </MetaRow>
          <MetaRow label="Modified" last>
            {screening.modified_at
              ? <span className="font-mono">{fmtDateTime(screening.modified_at)}</span>
              : <span className="italic text-slate-400">ยังไม่ถูกแก้ไข</span>}
          </MetaRow>
        </div>
      )}
    </div>
  )
}

function MetaRow({ label, children, last }) {
  return (
    <div className={`px-3 py-1.5 ${last ? '' : 'border-b border-brand-100'}`}>
      <div className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-brand-600">{label}</div>
      <div className="text-[12.5px] text-slate-700 leading-snug">{children}</div>
    </div>
  )
}
